'use client';

import {
  createContext,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useContext,
  useLayoutEffect,
  useState,
} from 'react';
import { usePathname } from '@/core/i18n/routing';
import {
  EMPTY_HEADER_SUMMARY_CONFIG,
  type HeaderSummaryConfig,
} from '@/core/services/uniform/fetch-page-header-summary-config';

const HeaderSummaryConfigContext = createContext<HeaderSummaryConfig | null>(
  null,
);

const SetHeaderSummaryConfigContext = createContext<Dispatch<
  SetStateAction<HeaderSummaryConfig>
> | null>(null);

const NON_CMS_PATH_PREFIXES = [
  '/account',
  '/cart',
  '/checkout',
  '/configurator',
  '/booking',
  '/support',
  '/orders',
  '/bookings',
] as const;

export { EMPTY_HEADER_SUMMARY_CONFIG };

export function HeaderSummaryConfigProvider({
  children,
  config: initialConfig,
}: {
  children: ReactNode;
  config: HeaderSummaryConfig;
}) {
  const [config, setConfig] = useState(initialConfig);

  return (
    <SetHeaderSummaryConfigContext.Provider value={setConfig}>
      <HeaderSummaryConfigContext.Provider value={config}>
        {children}
      </HeaderSummaryConfigContext.Provider>
    </SetHeaderSummaryConfigContext.Provider>
  );
}

/** Applies the current CMS page header summary config into layout context. */
export function HeaderSummaryConfigSync({
  config,
}: {
  config: HeaderSummaryConfig;
}) {
  const setConfig = useContext(SetHeaderSummaryConfigContext);

  useLayoutEffect(() => {
    setConfig?.(config);
  }, [config, setConfig]);

  return null;
}

/** Clears header summary on routes that are not Uniform CMS pages. */
export function HeaderSummaryNonCmsRouteReset() {
  const pathname = usePathname();
  const setConfig = useContext(SetHeaderSummaryConfigContext);

  useLayoutEffect(() => {
    if (NON_CMS_PATH_PREFIXES.some(prefix => pathname.startsWith(prefix))) {
      setConfig?.(EMPTY_HEADER_SUMMARY_CONFIG);
    }
  }, [pathname, setConfig]);

  return null;
}

export function useHeaderSummaryConfig(): HeaderSummaryConfig | null {
  return useContext(HeaderSummaryConfigContext);
}
